/**
 * Conexión nueva: un vecino sin servicio pasa a tenerlo (recorrido P9).
 *
 * En una sola operación se crea el arranque, se instala un medidor de bodega,
 * se abre el contrato, se carga el paquete de conexión en su cuenta y se abre
 * una OT de instalación que descuenta de bodega los materiales usados.
 *
 * El cobro es el **paquete del comité** (Configuración · Boleta): los
 * materiales dejan registrado el stock y el costo real de la obra, pero no
 * cambian lo que se le cobra al vecino.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plug, Plus, Trash2, UserCheck, X } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, Cargando, Input, Select, Textarea, useDescarteProtegido, useHayCambios } from "../../components/ui";
import { SocioAutocomplete } from "../../components/SocioAutocomplete";
import { formatRut } from "../../lib/rut";
import { fechaLarga } from "../../lib/format";
import { obtenerSocio, type SocioRead } from "../socios/api";
import { listarSectores } from "../sectores/api";
import { listarMedidores } from "../arranques/api";
import { listarTarifas } from "../tarifas/api";
import { listarUsuarios } from "../usuarios/api";
import { existenciasDeBodega, listarBodegas } from "../inventario/api";
import { fetchEmpresaConfiguracion } from "../empresa/api";
import { conectarVecino, type ConexionPayload } from "./api";
import { hoyLocalISO } from "../../lib/fechaLocal";

interface FilaMaterial {
  insumoId: string;
  cantidad: string;
}

function pesos(n: number): string {
  return `$${n.toLocaleString("es-CL")}`;
}

export function ConexionModal({
  onClose,
  socioInicialId = null,
}: {
  onClose: () => void;
  /** Titular preseleccionado (llega de Socios tras un alta: `?conexion=nueva&socio=`). */
  socioInicialId?: number | null;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  // Sólo se pide el socio preseleccionado; el resto se busca con el
  // autocompletado (antes se cargaba el padrón entero en un select).
  const socioInicialQ = useQuery({
    queryKey: ["socio", socioInicialId],
    queryFn: () => obtenerSocio(socioInicialId as number),
    enabled: socioInicialId != null,
  });
  const sectoresQ = useQuery({ queryKey: ["sectores"], queryFn: listarSectores });
  const medidoresQ = useQuery({
    queryKey: ["medidores", { en_bodega: true }],
    queryFn: () => listarMedidores(true),
  });
  const tarifasQ = useQuery({ queryKey: ["tarifas"], queryFn: listarTarifas });
  const usuariosQ = useQuery({
    queryKey: ["usuarios", { only_active: true }],
    queryFn: () => listarUsuarios(true),
  });
  const bodegasQ = useQuery({
    queryKey: ["inventario", "bodegas"],
    queryFn: listarBodegas,
  });
  const cfgQ = useQuery({
    queryKey: ["empresa", "configuracion"],
    queryFn: fetchEmpresaConfiguracion,
  });

  const [socio, setSocio] = useState<SocioRead | null>(null);
  // "datos" = formulario; "resumen" = revisión antes de conectar.
  const [paso, setPaso] = useState<"datos" | "resumen">("datos");
  const [direccion, setDireccion] = useState("");
  const [numeroCasa, setNumeroCasa] = useState("");
  const [sectorId, setSectorId] = useState("");
  const [referencia, setReferencia] = useState("");
  const [medidorId, setMedidorId] = useState("");
  const [lecturaInicial, setLecturaInicial] = useState("0");
  const [tarifaId, setTarifaId] = useState("");
  const [fecha, setFecha] = useState(hoyLocalISO());
  const [tecnicoId, setTecnicoId] = useState("");
  const [quiereSerSocio, setQuiereSerSocio] = useState(false);
  // null = usar el paquete del comité tal cual.
  const [montoEditado, setMontoEditado] = useState<string | null>(null);
  const [bodegaId, setBodegaId] = useState("");
  const [materiales, setMateriales] = useState<FilaMaterial[]>([]);
  const [observacion, setObservacion] = useState("");
  const [error, setError] = useState<string | null>(null);

  const existenciasQ = useQuery({
    queryKey: ["inventario", "existencias", "bodega", bodegaId],
    queryFn: () => existenciasDeBodega(Number(bodegaId)),
    enabled: bodegaId !== "",
  });

  const paquete = cfgQ.data?.cargo_conexion ?? 0;
  const cuota = cfgQ.data?.cuota_incorporacion ?? 0;
  const yaEraSocio = socio?.es_socio ?? false;
  const monto = montoEditado === null ? paquete : Number(montoEditado || 0);
  const cobraCuota = quiereSerSocio && !yaEraSocio && cuota > 0;

  function elegirSocio(s: SocioRead): void {
    if (s.estado === "Fallecido") {
      setError(`${s.nombre} figura como fallecido: no puede ser titular.`);
      return;
    }
    setError(null);
    setSocio(s);
    // La casa nueva suele ser donde vive: se propone, se puede cambiar.
    if (!direccion.trim() && s.direccion) setDireccion(s.direccion);
  }

  // Socio preseleccionado por la URL: se aplica una sola vez al llegar.
  useEffect(() => {
    if (socioInicialQ.data && socio == null) elegirSocio(socioInicialQ.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socioInicialQ.data]);

  const conStock = (existenciasQ.data ?? []).filter((e) => Number(e.cantidad) > 0);

  const mut = useMutation({
    mutationFn: (p: ConexionPayload) => conectarVecino(p),
    onSuccess: (r) => {
      toast.success(
        `Conectado · arranque ${r.arranque_codigo} · contrato #${r.contrato_id}` +
          ` · OT ${r.orden_trabajo_numero}`,
      );
      void qc.invalidateQueries({ queryKey: ["contratos"] });
      void qc.invalidateQueries({ queryKey: ["arranques"] });
      void qc.invalidateQueries({ queryKey: ["medidores"] });
      void qc.invalidateQueries({ queryKey: ["socios"] });
      void qc.invalidateQueries({ queryKey: ["inventario"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo conectar";
      setError(msg);
      toast.error(msg);
    },
  });

  const filas = materiales.filter((m) => m.insumoId && Number(m.cantidad) > 0);

  /** Valida y pasa al resumen. */
  function revisar(): void {
    setError(null);
    if (!socio) return setError("Elige a quién se conecta");
    if (!direccion.trim()) return setError("Falta la dirección");
    if (!medidorId) return setError("Elige un medidor de bodega");
    if (!tarifaId) return setError("Elige una tarifa");
    if (!fecha) return setError("Falta la fecha de conexión");
    if (fecha > hoyLocalISO()) return setError("La fecha de conexión no puede ser futura");
    if (filas.length > 0 && !bodegaId) return setError("Elige la bodega de los materiales");
    setPaso("resumen");
  }

  function enviar(): void {
    if (!socio) return;
    setError(null);
    mut.mutate({
      socio_id: socio.id,
      direccion: direccion.trim(),
      numero_casa: numeroCasa.trim() || null,
      sector_id: sectorId ? Number(sectorId) : null,
      referencia: referencia.trim() || null,
      medidor_id: Number(medidorId),
      tarifa_id: Number(tarifaId),
      lectura_inicial: Math.max(0, parseInt(lecturaInicial, 10) || 0),
      fecha: fecha || null,
      titular_socio: quiereSerSocio,
      ...(montoEditado === null ? {} : { monto_conexion: Math.max(0, monto) }),
      materiales: filas.map((m) => ({
        insumo_id: Number(m.insumoId),
        bodega_id: Number(bodegaId),
        cantidad: m.cantidad,
      })),
      tecnico_id: tecnicoId ? Number(tecnicoId) : null,
      observacion: observacion.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios(
    [
      socio?.id ?? null, direccion, numeroCasa, sectorId, referencia, medidorId,
      lecturaInicial, tarifaId, fecha, tecnicoId, quiereSerSocio, montoEditado,
      bodegaId, materiales, observacion,
    ],
    socioInicialId == null || socio != null || socioInicialQ.isError,
  );
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Conexión nueva"
      subtitle="Un vecino sin servicio pasa a tenerlo"
      icon={Plug}
      size="lg"
      closeOnBackdrop={false}
      footer={
        paso === "datos" ? (
          <>
            <Button variant="secondary" onClick={intentarCerrar}>
              Cancelar
            </Button>
            <Button onClick={revisar}>Revisar y conectar</Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => setPaso("datos")}
              disabled={mut.isPending}
            >
              Volver a editar
            </Button>
            <Button icon={Plug} onClick={enviar} disabled={mut.isPending}>
              {mut.isPending ? "Conectando…" : "Conectar"}
            </Button>
          </>
        )
      }
    >
      {paso === "resumen" && socio ? (
        <ResumenConexion
          socio={socio}
          direccion={[direccion.trim(), numeroCasa.trim()].filter(Boolean).join(" ")}
          sector={
            (sectoresQ.data ?? []).find((x) => String(x.id) === sectorId)?.nombre ?? null
          }
          referencia={referencia.trim() || null}
          medidor={(() => {
            const m = (medidoresQ.data ?? []).find((x) => String(x.id) === medidorId);
            return m ? `${m.numero_serie}${m.marca ? ` · ${m.marca}` : ""} · ${m.diametro}` : "—";
          })()}
          lecturaInicial={Math.max(0, parseInt(lecturaInicial, 10) || 0)}
          tarifa={(tarifasQ.data ?? []).find((x) => String(x.id) === tarifaId)?.nombre ?? "—"}
          fecha={fecha}
          tecnico={
            (usuariosQ.data ?? []).find((x) => String(x.id) === tecnicoId)?.nombre ??
            "Quien registra"
          }
          monto={Math.max(0, monto)}
          montoEditado={montoEditado !== null && monto !== paquete}
          cuota={cobraCuota ? cuota : 0}
          quedaComoSocio={quiereSerSocio || yaEraSocio}
          materiales={filas.length}
          error={error}
        />
      ) : (
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          Crea el arranque, instala el medidor, abre el contrato y deja una
          orden de trabajo de instalación. Si la persona no está en el padrón,
          dala de alta primero en Socios.
        </p>

        {/* ── Titular y casa ─────────────────────────────────────────── */}
        {socio ? (
          <div className="flex items-start justify-between gap-2 rounded-md border border-brand-200 bg-brand-50/50 p-3">
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-brand-700" strokeWidth={1.5} />
              <div>
                <div className="text-xs font-medium text-neutral-600">Titular</div>
                <div className="text-sm font-medium text-neutral-900">{socio.nombre}</div>
                <div className="text-xs text-neutral-600">
                  {formatRut(socio.rut)} · {socio.es_socio ? "socio" : "cliente"}
                </div>
              </div>
            </div>
            <Button variant="link" size="sm" icon={X} onClick={() => setSocio(null)}>
              Cambiar
            </Button>
          </div>
        ) : (
          <SocioAutocomplete
            label="Titular (nombre o RUT)"
            onSelect={elegirSocio}
            autoFocus
          />
        )}
        {socioInicialId != null && socioInicialQ.isPending && !socio && (
          <Cargando inline texto="Cargando el socio…" />
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Select
            label="Sector"
            value={sectorId}
            onChange={(e) => setSectorId(e.target.value)}
            disabled={mut.isPending}
          >
            <option value="">— sin sector —</option>
            {(sectoresQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre}
              </option>
            ))}
          </Select>
          <Input
            label="Dirección"
            required
            maxLength={255}
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            disabled={mut.isPending}
          />
          <Input
            label="N° de casa"
            maxLength={20}
            value={numeroCasa}
            onChange={(e) => setNumeroCasa(e.target.value)}
            disabled={mut.isPending}
          />
        </div>
        <Input
          label="Referencia"
          maxLength={255}
          placeholder="Frente a la escuela, portón verde…"
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
          disabled={mut.isPending}
        />

        {/* ── Medidor y contrato ─────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Select
            label={`Medidor en bodega (${medidoresQ.data?.length ?? 0})`}
            required
            value={medidorId}
            onChange={(e) => setMedidorId(e.target.value)}
            disabled={mut.isPending}
          >
            <option value="">— elegir —</option>
            {(medidoresQ.data ?? [])
              .filter((m) => m.estado !== "De Baja")
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.numero_serie}
                  {m.marca ? ` · ${m.marca}` : ""} · {m.diametro}
                </option>
              ))}
          </Select>
          <Input
            label="Lectura al instalar (m³)"
            type="number"
            min={0}
            value={lecturaInicial}
            onChange={(e) => setLecturaInicial(e.target.value)}
            disabled={mut.isPending}
          />
          <Select
            label="Tarifa"
            required
            value={tarifaId}
            onChange={(e) => setTarifaId(e.target.value)}
            disabled={mut.isPending}
          >
            <option value="">— elegir —</option>
            {(tarifasQ.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.nombre}
              </option>
            ))}
          </Select>
          <Input
            label="Fecha de conexión"
            type="date"
            max={hoyLocalISO()}
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            disabled={mut.isPending}
          />
          <Select
            label="Técnico a cargo"
            value={tecnicoId}
            onChange={(e) => setTecnicoId(e.target.value)}
            disabled={mut.isPending}
          >
            <option value="">— quien registra —</option>
            {(usuariosQ.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
                {u.cargo ? ` · ${u.cargo}` : ""}
              </option>
            ))}
          </Select>
        </div>

        {/* ── Lo que se cobra ────────────────────────────────────────── */}
        <section className="rounded-lg border border-neutral-200 p-3">
          <h4 className="text-sm font-medium text-neutral-900">Lo que se cobra</h4>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input
              label="Paquete de conexión ($)"
              type="number"
              min={0}
              value={montoEditado ?? String(paquete)}
              onChange={(e) => setMontoEditado(e.target.value)}
              disabled={mut.isPending}
            />
          </div>
          <p className="mt-1 text-xs text-neutral-600">
            {paquete > 0
              ? `El paquete del comité es ${pesos(paquete)} (instalación, medidor y materiales).`
              : "El comité no tiene paquete configurado: se define en Configuración · Boleta."}{" "}
            Se cobra en la próxima boleta, exento de IVA.
            {montoEditado !== null && monto !== paquete && (
              <span className="text-amber-700">
                {" "}
                Distinto del paquete: queda anotado en la bitácora.
              </span>
            )}
          </p>

          <label className="mt-3 flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={quiereSerSocio}
              onChange={(e) => setQuiereSerSocio(e.target.checked)}
              disabled={mut.isPending}
            />
            <span className="text-sm text-neutral-800">
              Quiere el derecho de socio
              <span className="block text-xs text-neutral-600">
                {yaEraSocio
                  ? "Ya es socio del comité: no paga la cuota de nuevo."
                  : cuota > 0
                    ? `Se le carga la cuota de incorporación de ${pesos(cuota)}. Si no, queda como cliente y su boleta lleva IVA.`
                    : "El comité no cobra cuota de incorporación. Si no la toma, queda como cliente y su boleta lleva IVA."}
              </span>
            </span>
          </label>
        </section>

        {/* ── Materiales de bodega ───────────────────────────────────── */}
        <section className="rounded-lg border border-neutral-200 p-3">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-neutral-900">
              Materiales usados
            </h4>
            <Button
              size="sm"
              variant="secondary"
              icon={Plus}
              className="ml-auto"
              onClick={() =>
                setMateriales((m) => [...m, { insumoId: "", cantidad: "1" }])
              }
              disabled={mut.isPending}
            >
              Agregar
            </Button>
          </div>
          <p className="mt-1 text-xs text-neutral-600">
            Se descuentan de bodega en la orden de trabajo. No cambian lo que
            se cobra. También se pueden cargar después, desde la OT.
          </p>
          {materiales.length > 0 && (
            <div className="mt-2 space-y-2">
              <Select
                label="Bodega"
                value={bodegaId}
                onChange={(e) => setBodegaId(e.target.value)}
                disabled={mut.isPending}
              >
                <option value="">— elegir —</option>
                {(bodegasQ.data ?? [])
                  .filter((b) => b.activa)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nombre}
                    </option>
                  ))}
              </Select>
              {materiales.map((fila, i) => (
                <div key={i} className="flex items-end gap-2">
                  <div className="flex-1">
                    <Select
                      label={i === 0 ? "Material" : undefined}
                      value={fila.insumoId}
                      onChange={(e) =>
                        setMateriales((m) =>
                          m.map((x, j) =>
                            j === i ? { ...x, insumoId: e.target.value } : x,
                          ),
                        )
                      }
                      disabled={mut.isPending || !bodegaId}
                    >
                      <option value="">— elegir —</option>
                      {conStock.map((e) => (
                        <option key={e.insumo_id} value={e.insumo_id}>
                          {e.insumo_nombre} · hay {Number(e.cantidad)}{" "}
                          {e.unidad_medida}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="w-28">
                    <Input
                      label={i === 0 ? "Cantidad" : undefined}
                      type="number"
                      min={0}
                      step="any"
                      value={fila.cantidad}
                      onChange={(e) =>
                        setMateriales((m) =>
                          m.map((x, j) =>
                            j === i ? { ...x, cantidad: e.target.value } : x,
                          ),
                        )
                      }
                      disabled={mut.isPending}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    icon={Trash2}
                    aria-label="Quitar material"
                    onClick={() =>
                      setMateriales((m) => m.filter((_, j) => j !== i))
                    }
                    disabled={mut.isPending}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        <Textarea
          label="Observación (queda en el cargo)"
          rows={2}
          placeholder="N° de solicitud, acuerdo del directorio, etc."
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          disabled={mut.isPending}
        />

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
      )}
      {dialogoDescarte}
    </Modal>
  );
}

function ResumenConexion({
  socio,
  direccion,
  sector,
  referencia,
  medidor,
  lecturaInicial,
  tarifa,
  fecha,
  tecnico,
  monto,
  montoEditado,
  cuota,
  quedaComoSocio,
  materiales,
  error,
}: {
  socio: SocioRead;
  direccion: string;
  sector: string | null;
  referencia: string | null;
  medidor: string;
  lecturaInicial: number;
  tarifa: string;
  fecha: string;
  tecnico: string;
  monto: number;
  montoEditado: boolean;
  cuota: number;
  quedaComoSocio: boolean;
  materiales: number;
  error: string | null;
}): JSX.Element {
  const total = monto + cuota;
  return (
    <div className="space-y-4 text-sm">
      <p className="text-neutral-700">
        Revisa antes de conectar: se crea el arranque, se instala el medidor, se
        abre el contrato y se carga el cobro en la cuenta del titular.
      </p>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 rounded-lg border border-neutral-200 p-3 sm:grid-cols-2">
        <Fila label="Titular" valor={`${socio.nombre} · ${formatRut(socio.rut)}`} />
        <Fila
          label="Queda como"
          valor={quedaComoSocio ? "Socio (boleta sin IVA)" : "Cliente (boleta con IVA)"}
        />
        <Fila
          label="Dirección"
          valor={[direccion, sector ? `Sector ${sector}` : null].filter(Boolean).join(" · ")}
        />
        {referencia && <Fila label="Referencia" valor={referencia} />}
        <Fila label="Medidor" valor={medidor} />
        <Fila label="Lectura al instalar" valor={`${lecturaInicial} m³`} />
        <Fila label="Tarifa" valor={tarifa} />
        <Fila label="Fecha de conexión" valor={fechaLarga(fecha)} />
        <Fila label="Técnico a cargo" valor={tecnico} />
        <Fila
          label="Materiales de bodega"
          valor={materiales === 0 ? "Ninguno (se pueden cargar en la OT)" : `${materiales} ítem(s)`}
        />
      </dl>
      <div className="rounded-lg border border-brand-200 bg-brand-50 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-brand-800">
          Lo que se cobra en la próxima boleta
        </div>
        <dl className="mt-2 space-y-1">
          <div className="flex justify-between gap-4">
            <dt>Paquete de conexión{montoEditado ? " (monto editado)" : ""}</dt>
            <dd className="tabular-nums">{pesos(monto)}</dd>
          </div>
          {cuota > 0 && (
            <div className="flex justify-between gap-4">
              <dt>Cuota de incorporación</dt>
              <dd className="tabular-nums">{pesos(cuota)}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4 border-t border-brand-200 pt-1 font-semibold text-neutral-900">
            <dt>Total</dt>
            <dd className="tabular-nums">{pesos(total)}</dd>
          </div>
        </dl>
        <p className="mt-1 text-xs text-neutral-700">Exento de IVA.</p>
      </div>
      {error && <p className="text-sm text-rose-700">{error}</p>}
    </div>
  );
}

function Fila({ label, valor }: { label: string; valor: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium text-neutral-600">{label}</dt>
      <dd className="text-neutral-900">{valor}</dd>
    </div>
  );
}
